import Constants from '@/Constants';
import { useGetPaginated } from '@/hooks/useGetPaginated';
import { SocialPerson } from '@/models/Person';
import { FullConfigurationWithExtraData } from '@/models/SWRCache';
import { ApplicationState } from '@/store';
import React, { useEffect, useState } from 'react';
import MagicSliderDots from 'react-magic-slider-dots';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import { useSWRConfig } from 'swr';
import Loader from '../Loader';
import PersonCard from '../PersonCard';

interface Props {
    onFinishSearch: Function;
}

const PeopleSection: React.FC<Props> = ({ onFinishSearch }: Props) => {
    const { searchTerm, doSearch } = useSelector((state: ApplicationState) => ({
        searchTerm: state.search.data.term,
        doSearch: state.search.doSearch,
    }));

    const SLIDES_TO_SHOW: number = 4;
    const SLIDES_TO_SCROLL: number = 4;

    const [currentTerm, setCurrentTerm] = useState<string>(searchTerm);

    const { cache } = useSWRConfig() as FullConfigurationWithExtraData;

    const { data, loading, total, loadMore } = useGetPaginated<SocialPerson>((index: number) => {
        const offset: number = index * Constants.PEOPLE_LIMIT;
        const queryParams: string = `?offset=${offset}&limit=${Constants.PEOPLE_LIMIT}&term=${currentTerm}`;

        if (currentTerm === '') {
            return null;
        }

        return `${Constants.CAST_PEOPLE_SEARCH_URI}${queryParams}`;
    });

    const handleLoadMore = (currentSlide: number): void => {
        const currentPage: number = Math.floor(currentSlide / SLIDES_TO_SHOW) + 1;
        const maxPage: number = Math.ceil(total / SLIDES_TO_SHOW);

        if (currentPage === maxPage) {
            return;
        }

        if (data.length < total && currentSlide >= data.length - SLIDES_TO_SHOW * 2) {
            loadMore();
        }
    };

    const getCarouselDots = (dots: boolean): JSX.Element => <MagicSliderDots dotWidth={30} dots={dots} numDotsToShow={4} />;

    useEffect(() => {
        if (doSearch) {
            setCurrentTerm(searchTerm);
        }
    }, [searchTerm]);

    useEffect(() => {
        if (data.length > 0) {
            onFinishSearch(true);
        }
    }, [data.length]);

    useEffect(() => {
        return () => {
            cache.clear();
        };
    }, []);

    return (
        <>
            {data?.length > 0 && (
                <div className="container carrosell-slide-container">
                    <Slider
                        afterChange={handleLoadMore}
                        appendDots={getCarouselDots}
                        dots={true}
                        infinite={false}
                        slidesToScroll={SLIDES_TO_SCROLL}
                        slidesToShow={SLIDES_TO_SHOW}
                        speed={500}
                    >
                        {data.map((person: SocialPerson) => (
                            <PersonCard key={person.id} person={person} />
                        ))}
                    </Slider>
                </div>
            )}

            {loading && data?.length === 0 && <Loader />}
        </>
    );
};

export default PeopleSection;
